import { PurchaseTypeEnum } from 'api-hooks/order/common/model';
import { HotelPurchaseModel } from 'api-hooks/order/hotel';
import { PurchaseRelatedModel } from 'api-hooks/sale/common';
import { HotelSaleModel } from 'api-hooks/sale/hotel';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { themedStyled } from 'common/styles/styletron.theme';
import { capitalize, generateId } from 'common/utils/string';
import Separator from 'components/common/separator';
import { Input } from 'components/elements';
import { useFormState } from 'components/elements/form';
import Text from 'components/elements/text';
import AgentSelectInput from 'legacy-modules/master/agent/components/select-input';
import PaymentChannelSelectInput from 'legacy-modules/master/payment-channel/components/select-input';
import AgentSupendToast from 'legacy-modules/order/component/agent-suspend-toast';
import PurchaseRelatedTable from 'legacy-modules/sales/component/purchase-related-table';
import TransactionAt from 'legacy-modules/sales/component/transaction-at';
import HotelSaleInvoiceSelectInput from 'legacy-modules/sales/hotel/components/invoice-select-input';
import { guestNameTransformer } from 'legacy-modules/sales/ticket-sale/hooks/use-get-default-values';
import {
  CardSection,
  HalfContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import TourListSelectInput from 'legacy-modules/tour/tour-list/components/select-input';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  HotelPurchaseFormType,
  HotelPurchaseHotelItemFormType,
  HotelPurchaseItemFormType,
} from './form-type';

interface Props {
  hotelPurchase?: HotelPurchaseModel;
}

interface PurchaseDetailCardProps extends Props {
  setPurchases: React.Dispatch<React.SetStateAction<PurchaseRelatedModel[]>>;
}

function PurchaseDetailCardComponent(props: PurchaseDetailCardProps) {
  const { t } = useTranslation();
  const { setValue, control, clearErrors, trigger, getValues, reset } =
    useFormContext<HotelPurchaseFormType>();
  const [type, hotelSale, total, remainingTransactionQuota] = useWatch({
    control,
    name: ['type', 'hotelSale', 'total', 'remainingTransactionQuota'],
  });
  const { editable } = useFormState();

  const isExceedingQuota =
    remainingTransactionQuota && total > remainingTransactionQuota;

  React.useEffect(() => {
    setValue('isExceedingQuota', isExceedingQuota || false);
  }, [setValue, isExceedingQuota]);

  const invoiceId = getValues('invoiceId');
  React.useEffect(() => {
    if (!invoiceId || invoiceId === '') {
      reset();
      props.setPurchases([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, reset]);

  const tourInvoiceOnAfterChange = React.useCallback(
    (value: TourModel) => {
      if (editable) {
        const hotelPurchases = value.tourHotels
          .map((th) => {
            return th.details.map((detail) => {
              return {
                bookingCode: '',
                hotelId: th.hotel.id,
                hotelName: th.hotel.name,
                isByPax: false,
                hotelPurchaseGuests: [],
                hotelPurchaseHotels: [
                  {
                    checkIn: detail.checkIn,
                    checkOut: detail.checkOut,
                    hotelRoomId: detail.hotelRoom.id,
                    price: 0,
                    night: detail.night,
                    customId: generateId(),
                    detail: '',
                    hotelRoomName: `${detail.hotelRoom.type} (${detail.hotelRoom.size})`,
                    id: undefined,
                    pax: undefined,
                    room: '',
                  } as never as HotelPurchaseHotelItemFormType,
                ],
                note: th.note,
                cityTax: '0',
                isCityTax: false,
                customId: generateId(),
                defaultCheckInOut: {
                  in: '',
                  out: '',
                },
                hotelAddress: th.hotel.address || '',
                hotelEmail: th.hotel.email || '',
                hotelPhone: th.hotel.phone || '',
                hotelPurchaseAddOns: [],
                remark: '',
                id: undefined,
              } as HotelPurchaseItemFormType;
            });
          })
          .flat();

        setValue('hotelPurchases', hotelPurchases, {
          shouldTouch: true,
          shouldValidate: true,
        });

        clearErrors('hotelPurchases');
      }
    },
    [editable, setValue, clearErrors],
  );

  const invoiceOnAfterChange = React.useCallback(
    (value: HotelSaleModel) => {
      if (editable) {
        if (!props.hotelPurchase) {
          props.setPurchases(value.purchases);
        }
        const hotelPurchases: HotelPurchaseItemFormType[] =
          value.hotelSales.map((hs) => {
            const hotelPurchaseHotels: HotelPurchaseItemFormType['hotelPurchaseHotels'] =
              hs.hotelSaleHotels.map((hsh) => {
                return {
                  checkIn: hsh.checkIn,
                  checkOut: hsh.checkOut,
                  detail: hsh.detail || '',
                  hotelRoomId: hsh.hotelRoom.id,
                  hotelRoomName: `${hsh.hotelRoom.type} (${hsh.hotelRoom.size})`,
                  price: hsh.price,
                  room: hsh.room,
                  night: hsh.night,
                  pax: hsh.pax || 0,
                  customId: generateId(),
                  id: undefined,
                };
              });

            const hotelPurchaseAddOns: HotelPurchaseItemFormType['hotelPurchaseAddOns'] =
              hs.hotelSaleAddOns.map((hsao) => {
                const guest = hsao.guest;
                return {
                  guestId: guest.id,
                  guestName: guestNameTransformer(guest),
                  description: hsao.description || '',
                  price: hsao.price,
                  priceUnit: hsao.priceUnit || 0,
                  unit: hsao.unit || 0,
                  customId: generateId(),
                  id: undefined,
                };
              });

            const hotelPurchaseGuests: HotelPurchaseItemFormType['hotelPurchaseGuests'] =
              hs.hotelSaleGuests.map((hsg) => {
                return {
                  guestId: hsg.guest.id,
                  guestName: `${capitalize(hsg.guest.title)} ${hsg.guest.name}`,
                  isLead: hsg.isLead,
                  customId: generateId(),
                  id: undefined,
                };
              });

            const hotel = hs.hotel;
            return {
              bookingCode: hs.bookingCode || '',
              hotelId: hs.hotel.id,
              hotelName: hs.hotel.name,
              hotelPurchaseGuests,
              hotelPurchaseHotels,
              hotelPurchaseAddOns,
              cityTax: hs.cityTax || '',
              isCityTax: hs.isCityTax,
              isByPax: hs.isByPax,
              customId: generateId(),
              defaultCheckInOut: {
                in: '',
                out: '',
              },
              hotelAddress: hotel.address,
              hotelEmail: hotel.email,
              hotelPhone: hotel.phone,
              id: undefined,
              remark: '',
            };
          });
        setValue('hotelPurchases', hotelPurchases);
        setValue('discount', value.discount);
        setValue('otherCost', value.otherCost);
        setValue('saleTotal', value.total);
        setValue('invoiceRefId', value.refId);

        trigger([
          'hotelPurchases',
          'discount',
          'otherCost',
          'saleTotal',
          'invoiceRefId',
        ]);
      }
    },
    [editable, props, setValue, trigger],
  );

  const onEnumAfterChange = React.useCallback(() => {
    props.setPurchases([]);
    setValue('invoiceRefId', undefined, {
      shouldTouch: true,
      shouldValidate: true,
    });
    setValue('tourRefId', undefined, {
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [props, setValue]);

  const agentId = getValues('agentId');
  React.useEffect(() => {
    if (!agentId || agentId === '') {
      setValue('agentStatus', '');
      setValue('remainingTransactionQuota', null);
      setValue('isExceedingQuota', false);
    }
  }, [agentId, setValue]);

  const onAfterAgentDetailChange = React.useCallback(
    (result) => {
      if (editable) {
        const remainingTransactionQuota =
          result.system.remainingTransactionQuota;
        const isExceedingQuota =
          result.system.isTransactionLimited &&
          total > remainingTransactionQuota;

        setValue('isExceedingQuota', isExceedingQuota);
        setValue(
          'remainingTransactionQuota',
          remainingTransactionQuota || null,
        );
        setValue('agentStatus', result.status);
      }
    },
    [editable, setValue, total],
  );

  const disabled = !!props.hotelPurchase || !!hotelSale;

  return (
    <CardSection>
      <Text variant="HeadingXSmall">{t('ticketOrders:orders-details')}</Text>
      <SeparatorVertical $margin={24} />
      <SectionContainer>
        {props.hotelPurchase && (
          <HalfContainer>
            <Input
              type="text"
              name="orderNumber"
              label={t('common:order_number')}
              placeholder={t('common:order_number')}
              disabled
            />
          </HalfContainer>
        )}
        <HalfContainer
          $style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <TransactionAt
            name="transactionAt"
            clearable={false}
            hasTime
            required
          />
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <Input
            type="select"
            label={t('common:type')}
            placeholder={t('common:type')}
            name="type"
            required
            onAfterChange={onEnumAfterChange}
            clearable={false}
            disabled={disabled}
            options={[
              { id: PurchaseTypeEnum.Sales, label: t('common:sales') },
              { id: PurchaseTypeEnum.Tour, label: t('common:tour') },
            ]}
          />
        </HalfContainer>
        <HalfContainer>
          {type === PurchaseTypeEnum.Sales ? (
            <HotelSaleInvoiceSelectInput
              label={t('common:invoice_number')}
              placeholder={t('common:choose_invoice')}
              name="invoiceId"
              onAfterDetailChange={invoiceOnAfterChange}
              disabled={disabled}
              required
            />
          ) : (
            type === PurchaseTypeEnum.Tour && (
              <TourListSelectInput
                name="tourId"
                required
                disabled={!!props.hotelPurchase}
                onAfterDetailChange={tourInvoiceOnAfterChange}
              />
            )
          )}
        </HalfContainer>
      </SectionContainer>
      <SectionContainer>
        <HalfContainer>
          <AgentSelectInput
            name="agentId"
            remainingTransactionQuota={remainingTransactionQuota}
            onAfterDetailChange={onAfterAgentDetailChange}
            required
          />
        </HalfContainer>
        <HalfContainer>
          <PaymentChannelSelectInput name="paymentChannelId" />
        </HalfContainer>
      </SectionContainer>
      <AgentSupendToast />
    </CardSection>
  );
}

export default function PurchasesDetailCard(props: Props) {
  const { t } = useTranslation();
  const [purchases, setPurchases] = React.useState<PurchaseRelatedModel[]>([]);
  const { control } = useFormContext<HotelPurchaseFormType>();
  const [type] = useWatch({
    control,
    name: ['type'],
  });

  return (
    <>
      <PurchaseDetailCardComponent {...props} setPurchases={setPurchases} />
      {type === PurchaseTypeEnum.Sales &&
        !props.hotelPurchase &&
        !!purchases.length && (
          <>
            <Separator gap={24} />
            <Text variant="HeadingXSmall">{t('common:related_purchases')}</Text>
            <Separator gap={8} />
            <PurchaseRelatedTable
              data={purchases}
              navigation="HotelPurchasesView"
            />
          </>
        )}
      <Separator gap={24} />
    </>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
});
